import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/services/base/api.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { IBaseApiResponse } from 'app/shared/services/base/base-api.service';
import { catchError, Observable, throwError } from 'rxjs';
import { IExchangeResponse, IImportStateResponse, IRefreshResponse } from '../../models';

@Injectable({ providedIn: 'root' })
export class LinkedInImportApiService extends ApiService {
    constructor(http: HttpClient, appConfig: AppConfigService) {
        super(http, appConfig);
    }

    /** Redeems the single-use exchange code from the magic-link redirect for a bearer + refresh token pair. */
    exchangeCode$(code: string): Observable<IBaseApiResponse<IExchangeResponse>> {
        return this.postWithReturnObject('LinkedInImport/exchange', { code });
    }

    /** Silently re-mints a bearer token from the (reusable, within its window) refresh token. */
    refresh$(refreshToken: string): Observable<IBaseApiResponse<IRefreshResponse>> {
        return this.postWithReturnObject('LinkedInImport/refresh', { refreshToken });
    }

    /** BL-413: the first-ever in-app trigger for the desktop magic-link flow -- kicks off
     * RequestSetupLink from an Entra-session context (the ImportContacts empty-state CTA), not
     * from the emailed link. Already excluded from linkedin-import-auth.interceptor.ts's bearer
     * attachment, so this rides the app's normal Entra/MSAL auth like any other call. */
    requestSetupLink$(): Observable<void> {
        return this.http
            .post<void>(this.url('LinkedInImport/setup-link'), {})
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }

    // getState$/markRequested$/retry$/upload$ deliberately bypass postWithReturnObject/getObject$
    // and catch the raw HttpErrorResponse instead. ApiService's typed helpers route errors through
    // handleTypedError/handleError, both of which discard the original HTTP status code -- fine
    // for exchangeCode$/refresh$ above (every failure there already collapses to the same
    // "recovery" outcome), but wrong here: telling a definitive 401/410 (session dead -> recovery)
    // apart from a transient network/5xx blip (keep the current status, don't blow away
    // "processing" for an import that's about to succeed) requires the real status code.

    /** The single source of truth driving the page's status. Also the polling endpoint while an
     * import runs. */
    getState$(): Observable<IImportStateResponse> {
        return this.http
            .get<IImportStateResponse>(this.url('LinkedInImport/state'))
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }

    /** Records "I requested it". Returns fresh state directly -- no forced follow-up GET /state. */
    markRequested$(): Observable<IImportStateResponse> {
        return this.http
            .post<IImportStateResponse>(this.url('LinkedInImport/requested'), {})
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }

    /** Revives a Failed attempt. Returns fresh state directly, same as markRequested$. */
    retry$(): Observable<IImportStateResponse> {
        return this.http
            .post<IImportStateResponse>(this.url('LinkedInImport/retry'), {})
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }

    /** Multipart upload with progress events -- ApiService.request() is otherwise unused in the app. */
    upload$(file: File): Observable<HttpEvent<void>> {
        return this.request<void>('LinkedInImport/upload', file);
    }

    /** Recovery-state action. Authenticates via the reply token (sessionStorage, distinct from the
     * bearer) in the JSON body -- NOT an Authorization header, matching BL-415's actual contract. */
    resendSetupLink$(replyToken: string): Observable<void> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http
            .post<void>(this.url('LinkedInImport/resend-setup-link'), { replyToken }, { headers })
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }
}
